import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Anchor, Shield, Award, Wrench } from 'lucide-react'
import { Container } from '@/components/layout'
import { SEO } from '@/components/SEO'
import { useCategories } from '@/hooks/useCategories'
import { useHeroSettings } from '@/hooks/useSiteSettings'
import { stripHtml } from '@/lib/utils'

const features = [
  {
    icon: Shield,
    title: 'Certified Quality',
    description: 'All products certified by DNV/GL, BV, Lloyds, RINA, and ABS',
  },
  {
    icon: Wrench,
    title: 'Custom Solutions',
    description: 'Made-to-order sizing and custom configurations available',
  },
  {
    icon: Award,
    title: 'Industry Experience',
    description: 'Decades of experience in marine equipment manufacturing',
  },
]

export function HomePage() {
  const { data: categories, isLoading: categoriesLoading } = useCategories()
  const { data: heroSettings } = useHeroSettings()

  return (
    <>
      <SEO pageKey="home" fallback={{ title: 'EngiMarineX - Premium Marine Equipment & Solutions' }} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-secondary-900 via-secondary-800 to-primary-900 text-white overflow-hidden">
        {/* Background Image */}
        {heroSettings?.background_image && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroSettings.background_image})`,
              opacity: (heroSettings.background_opacity || 30) / 100,
            }}
          />
        )}
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-900/80 via-secondary-800/70 to-primary-900/80" />
        {/* Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('/images/hero-pattern.svg')] opacity-5" />
        <Container className="relative py-20 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            {heroSettings?.logo_image && (
              <div className="mb-6">
                {heroSettings.logo_link ? (
                  <a
                    href={heroSettings.logo_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={heroSettings.logo_image}
                      alt="Hero logo"
                      className="h-16 lg:h-20 w-auto object-contain"
                    />
                  </a>
                ) : (
                  <img
                    src={heroSettings.logo_image}
                    alt="Hero logo"
                    className="h-16 lg:h-20 w-auto object-contain"
                  />
                )}
              </div>
            )}
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6">
              {heroSettings?.title || 'Marine Equipment'}{' '}
              <span className="text-primary-400">{heroSettings?.title_highlight || 'Solutions'}</span>
            </h1>
            <p className="text-lg lg:text-xl text-secondary-300 mb-8 leading-relaxed">
              {heroSettings?.description || 'High-quality doors, hatches, valves, and deck equipment for the maritime industry. Certified by leading classification societies.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products" className="btn-primary px-6 py-3 text-base">
                {heroSettings?.button1_text || 'Explore Products'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/contact" className="btn-outline border-white text-white hover:bg-white/10 px-6 py-3 text-base">
                {heroSettings?.button2_text || 'Contact Us'}
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Categories Section */}
      <section className="py-16 lg:py-24 bg-secondary-50">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-secondary-900 mb-4">
              Our Products
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
              Comprehensive range of marine equipment designed and manufactured
              to the highest standards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categoriesLoading ? (
              // Loading skeleton
              [...Array(8)].map((_, i) => (
                <div key={i} className="card animate-pulse">
                  <div className="aspect-square bg-secondary-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-5 bg-secondary-200 rounded w-2/3" />
                    <div className="h-3 bg-secondary-200 rounded w-full" />
                  </div>
                </div>
              ))
            ) : (
              categories?.map((category, index) => {
                // Priority: 1. Admin-set image_url, 2. Auto-generated preview from products
                const primaryImg = category.image_url || category.preview_images?.primary
                // Priority: 1. Admin-set hover_image_url, 2. Auto-generated preview from products
                const secondaryImg = category.hover_image_url || category.preview_images?.secondary
                const hasSecondImage = !!secondaryImg

                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/products/${category.slug}`}
                      className="group block card-hover h-full"
                    >
                      <div
                        className="aspect-square bg-white relative overflow-hidden"
                        style={{ perspective: '1000px' }}
                      >
                        {primaryImg ? (
                          <div
                            className={`relative w-full h-full transition-transform duration-500 ${
                              hasSecondImage ? '[transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]' : ''
                            }`}
                          >
                            {/* Front - Primary Image */}
                            <img
                              src={primaryImg}
                              alt={category.name}
                              className={`absolute inset-0 w-full h-full object-cover ${
                                hasSecondImage ? '[backface-visibility:hidden]' : ''
                              }`}
                            />
                            {/* Back - Secondary Image */}
                            {hasSecondImage && (
                              <img
                                src={secondaryImg}
                                alt={category.name}
                                className="absolute inset-0 w-full h-full object-cover [backface-visibility:hidden] [transform:rotateY(180deg)]"
                              />
                            )}
                          </div>
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Anchor className="w-12 h-12 text-secondary-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/60 to-transparent pointer-events-none" />
                      </div>
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-secondary-900 mb-1 group-hover:text-primary-600 transition-colors">
                          {category.name}
                        </h3>
                        <p className="text-secondary-600 text-xs line-clamp-2">
                          {category.description ? stripHtml(category.description) : 'Explore our products'}
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                )
              })
            )}
          </div>

          <div className="text-center mt-10">
            <Link to="/products" className="btn-primary px-6 py-3">
              View All Products
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl mb-6">
                  <feature.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary-600">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Contact our team for custom quotes, technical specifications, or
              any questions about our products.
            </p>
            <Link
              to="/contact"
              className="btn bg-white text-primary-600 hover:bg-primary-50 px-8 py-3 text-base font-semibold"
            >
              Contact Us Today
            </Link>
          </motion.div>
        </Container>
      </section>
    </>
  )
}
